import { Link, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp, useUser } from "@clerk/react";
import { Heart } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { useSaved } from "@/lib/shopper";
import { DealGrid, DealGridSkeleton } from "@/components/site/DealCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActivityTab, PrivacyTab, ProfileTab } from "@/components/site/AccountTabs";

const AuthPage = ({ children }) => (
  <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="shopper-auth-page">{children}</div>
);

export const SignInPage = () => {
  useSeo({ title: "Sign in", noindex: true });
  return <AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>;
};

export const SignUpPage = () => {
  useSeo({ title: "Create account", noindex: true });
  return <AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>;
};

const Saved = () => {
  const { user } = useUser();
  const { items, loading } = useSaved();
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-6" data-testid="account-page">
      <div>
        <h1 className="text-3xl font-black">Hi {user?.firstName || "there"}</h1>
        <p className="text-slate-600 text-sm" data-testid="account-email">{user?.primaryEmailAddress?.emailAddress}</p>
      </div>
      <Tabs defaultValue="saved">
        <TabsList className="mb-6">
          <TabsTrigger value="saved" data-testid="account-tab-saved"><Heart size={14} className="mr-1 text-red-500" /> Saved</TabsTrigger>
          <TabsTrigger value="profile" data-testid="account-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="activity" data-testid="account-tab-activity">My activity</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="account-tab-privacy">Privacy</TabsTrigger>
        </TabsList>
        <TabsContent value="saved">
          {loading ? <DealGridSkeleton /> : items.length ? <DealGrid deals={items} testid="saved-deals-grid" /> : (
            <p className="text-slate-600" data-testid="saved-empty">No saved deals yet. Tap the heart on any deal to keep it here. <Link to="/deals" className="text-emerald-700 font-semibold">Browse deals</Link></p>
          )}
          <p className="text-xs text-slate-500 mt-4">Prices and expiry can change - open a deal to see the latest.</p>
        </TabsContent>
        <TabsContent value="profile"><ProfileTab /></TabsContent>
        <TabsContent value="activity"><ActivityTab /></TabsContent>
        <TabsContent value="privacy"><PrivacyTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export const AccountPage = () => {
  useSeo({ title: "My account", noindex: true });
  return (
    <>
      <Show when="signed-in"><Saved /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
};
